import {
  COMPONENT_VALUE_TYPES as types,
  ALL_POSITIONS,
  DIRECTIONS,
  COMPONENT_COLOR_THEMES,
  FONT_FAMILIES,
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

import lodashRange from 'lodash/range';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

// inclusive range
const range = (start: number, end: number, step: number) => lodashRange(start, end + step, step);

export type BackgroundImage = {
    original?: string;
    mobile?: string;
    tablet?: string;
    desktop: string;
}

export interface Props {
  colorTheme: 'DARK' | 'LIGHT',
  ctaSection: {
    button: {
      text: string;
      url: string;
    },
    titleFont: string;
    title: string;
    descriptionFont: string;
    descriptionLineHeight: string;
    description: string;
    margin: string;
    position: typeof ALL_POSITIONS[number]
  },
  height: string;
  backgroundImage?: BackgroundImage | string;
  linearGradient: {
    enabled: boolean;
    opacity: string;
    degree: string;
    colorList: {
      color: string;
      stop: string;
    }[],
  },
  animation: {
    fadeIn: {
      enabled: boolean;
      duration: string;
    },
    slideIn: {
      enabled: boolean;
      duration: string;
      direction: typeof DIRECTIONS[number];
    }
  }
}

const propsSchema: PropsSchemaList = [
  {
    key: 'colorTheme',
    label: 'Color Theme',
    type: types.ENUM,
    options: COMPONENT_COLOR_THEMES
  },
  {
    key: 'ctaSection',
    label: 'CTA',
    type: types.OBJECT,
    children: [
      {
        key: 'position',
        label: 'Position',
        type: types.ENUM,
        options: ALL_POSITIONS
      },
      {
        key: 'titleFont',
        label: 'Title Font',
        type: types.ENUM,
        options: FONT_FAMILIES
      },
      {
        key: 'title',
        label: 'Title',
        type: types.RICHTEXT
      },
      {
        key: 'descriptionFont',
        label: 'Description Font',
        type: types.ENUM,
        options: FONT_FAMILIES
      },
      {
        key: 'descriptionLineHeight',
        label: 'Description Line Height',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Description',
        type: types.RICHTEXT
      },
      {
        key: 'button',
        label: 'Button',
        type: types.OBJECT,
        children: [
          {
            key: 'text',
            label: 'Text',
            type: types.TEXT
          },
          {
            key: 'url',
            label: 'URL',
            type: types.TEXT
          },
        ]
      },
      {
        key: 'margin',
        label: 'Margin (px)',
        type: types.ENUM,
        options: range(0, 50, 2)
      },
    ]

  },
  {
    key: 'height',
    label: 'Height (if no background image)',
    type: types.ENUM,
    options: range(300, 1000, 10)
  },
  {
    key: 'backgroundImage',
    label: 'Background Image',
    type: types.S3_ASSET_URL,
    options: {
      cropEnabled: true,
    },
  },
  {
    key: 'linearGradient',
    label: 'Linear Gradient Overlay',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enabled',
        type: types.BOOLEAN
      },
      {
        key: 'opacity',
        label: 'opacity %',
        type: types.ENUM,
        options: range(0, 100, 1)
      },
      {
        key: 'degree',
        label: 'Degree °',
        type: types.ENUM,
        options: range(0, 360, 1)
      },
      {
        key: 'colorList',
        label: 'Color List',
        type: types.ARRAY,
        children: [
          {
            key: 'color',
            label: 'Color',
            type: types.COLOR_PICKER
          },
          {
            key: 'stop',
            label: 'Stop (%)',
            type: types.ENUM,
            options: range(0, 100, 1)
          },
        ]
      },
    ]
  },
  {
    key: 'animation',
    label: 'Animation',
    type: types.OBJECT,
    children: [
      {
        key: 'fadeIn',
        label: 'Fade In',
        type: types.OBJECT,
        children: [
          {
            key: 'enabled',
            label: 'Enabled',
            type: types.BOOLEAN
          },
          {
            key: 'duration',
            label: 'Duration (seconds)',
            type: types.ENUM,
            options: range(0, 30, 1).map(x => x / 10)
          },
        ]
      },
      {
        key: 'slideIn',
        label: 'Slide In',
        type: types.OBJECT,
        children: [
          {
            key: 'enabled',
            label: 'Enabled',
            type: types.BOOLEAN
          },
          {
            key: 'duration',
            label: 'Duration (seconds)',
            type: types.ENUM,
            options: range(0, 30, 1).map(x => x / 10)
          },
          {
            key: 'direction',
            label: 'Direction',
            type: types.ENUM,
            options: DIRECTIONS
          },
        ]
      },
    ]
  },
];

const defaultProps: Props = {
  colorTheme: 'LIGHT',
  ctaSection: {
    button: {
      text: 'Button',
      url: 'https://www.ff.com'
    },
    titleFont: FONT_FAMILY_ROBOTO,
    title: '<p>Title</p>',
    descriptionFont: FONT_FAMILY_ROBOTO,
    descriptionLineHeight: 'normal',
    description: '<p>Description</p>',
    margin: '50',
    position: 'TOP_LEFT'
  },
  height: '500',
  backgroundImage: undefined,
  linearGradient: {
    enabled: true,
    opacity: '10',
    degree: '0',
    colorList: [
      {
        color: '#ffffff',
        stop: '0'
      },
      {
        color: '#000000',
        stop: '100'
      }
    ],
  },
  animation: {
    fadeIn: {
      enabled: false,
      duration: '1'
    },
    slideIn: {
      enabled: true,
      duration: '1',
      direction: 'RIGHT'
    }
  }
};

export default {
  title: 'CTA Widget',
  id: 'CTAWidget',
  propsSchema,
  defaultProps
};
